import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Gestor genérico de motivos configuráveis (stop-reasons + defect-types).
 * Props:
 *  - kind: 'stop' | 'defect'
 */
export default function ReasonManager({ kind }) {
  const isStop = kind === "stop";
  const endpoint = isStop ? "stop-reasons" : "defect-types";
  const label = isStop ? "Motivos de Paragem" : "Motivos de Rejeição (Defeitos)";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(isStop
    ? { code: "", description: "", stop_type: "unplanned" }
    : { code: "", description: "", defect_category: "visual", responsible_area: "production" }
  );

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/${endpoint}`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [kind]); // eslint-disable-line

  const create = async () => {
    if (!form.code || !form.description) {
      toast.error("Código e descrição são obrigatórios");
      return;
    }
    try {
      await axios.post(`${API}/${endpoint}`, form);
      toast.success("Adicionado");
      setForm(isStop
        ? { code: "", description: "", stop_type: "unplanned" }
        : { code: "", description: "", defect_category: "visual", responsible_area: "production" }
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Desactivar este motivo?")) return;
    try {
      await axios.delete(`${API}/${endpoint}/${id}`);
      toast.success("Removido");
      load();
    } catch (e) {
      // fallback: alguns endpoints só desactivam
      try {
        await axios.put(`${API}/${endpoint}/${id}`, { active: false });
        toast.success("Desactivado");
        load();
      } catch (e2) {
        toast.error(e2?.response?.data?.detail || "Erro");
      }
    }
  };

  return (
    <div className="space-y-4" data-testid={`reason-manager-${kind}`}>
      <div>
        <h3 className="text-lg font-bold mb-1">{label}</h3>
        <p className="text-sm text-gray-500">Os operadores selecionam estes motivos ao pausar ou rejeitar peças.</p>
      </div>

      {/* Novo */}
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Código *</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder={isStop ? "MP01" : "REB" } data-testid={`${kind}-code-input`} />
            </div>
            <div className="md:col-span-2">
              <Label>Descrição *</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={isStop ? "Espera por MP" : "Rebarba"} data-testid={`${kind}-desc-input`} />
            </div>
            {isStop ? (
              <div>
                <Label>Tipo</Label>
                <Select value={form.stop_type} onValueChange={(v) => setForm({ ...form, stop_type: v })}>
                  <SelectTrigger data-testid="stop-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planned">Planeada</SelectItem>
                    <SelectItem value="unplanned">Não Planeada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label>Categoria</Label>
                <Select value={form.defect_category} onValueChange={(v) => setForm({ ...form, defect_category: v })}>
                  <SelectTrigger data-testid="defect-cat-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="visual">Visual</SelectItem>
                    <SelectItem value="dimensional">Dimensional</SelectItem>
                    <SelectItem value="functional">Funcional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {!isStop && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-start-4">
                <Label>Área Responsável</Label>
                <Select value={form.responsible_area} onValueChange={(v) => setForm({ ...form, responsible_area: v })}>
                  <SelectTrigger data-testid="defect-area-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="production">Produção</SelectItem>
                    <SelectItem value="quality">Qualidade</SelectItem>
                    <SelectItem value="maintenance">Manutenção</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <Button onClick={create} className="w-full md:w-auto" data-testid={`${kind}-add-btn`}>
            <Plus className="h-4 w-4 mr-1" /> Adicionar
          </Button>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardContent className="pt-6">
          {loading ? <p className="text-sm text-gray-500">A carregar...</p> :
            items.length === 0 ? <p className="text-sm text-gray-500 italic">Sem motivos configurados.</p> :
              <div className="space-y-2">
                {items.map(item => (
                  <div key={item.id} className={`flex items-center justify-between p-2 rounded border ${item.active === false ? "bg-gray-100 opacity-60" : "bg-white"}`}>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs px-2 py-1 rounded bg-slate-100 text-slate-800">{item.code}</span>
                      <div>
                        <p className="text-sm font-medium">{item.description}</p>
                        <p className="text-xs text-gray-500">
                          {isStop ? (item.stop_type === "planned" ? "Planeada" : "Não Planeada")
                            : `${item.defect_category} · ${item.responsible_area}`}
                        </p>
                      </div>
                    </div>
                    <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(item.id)} data-testid={`${kind}-remove-${item.code}`}>
                      {item.active === false ? <RotateCcw className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </div>
                ))}
              </div>
          }
        </CardContent>
      </Card>
    </div>
  );
}
